import {
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import React, { useEffect, useState } from "react";
import Link from "@mui/material/Link";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

interface Props {
  rows: any[];
}

const PerformanceDashboardBottomRatedPages = ({ rows }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(() => Number(searchParams.get("brpPage")) || 0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const { selectedSharedUid } = useSelector((state: any) => state.pageSpeedPerformance);

  useEffect(() => {
    setPage(0);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("brpPage");
      return next;
    }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSharedUid]);
  const [sort, setSort] = useState<{
    keytoSort: string | undefined;
    direction: "asc" | "desc" | undefined;
  }>({
    keytoSort: "Overall_Speed_Performance_Score",
    direction: "asc",
  });

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = +event.target.value;
    if (newRowsPerPage !== rowsPerPage) {
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    }
  };

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable

    setSort({
      keytoSort: header.KEY,
      // Toggle sort direction if the same column is clicked, otherwise default to ascending
      direction:
        header.KEY === sort.keytoSort && sort.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  const tableHeaders = [
    { id: 0, KEY: "page_name", sortable: true, label: "Page Name" },
    {
      id: 1,
      KEY: "Overall_Speed_Performance_Score",
      sortable: true,
      label: "Overall Score",
    },
    {
      id: 2,
      KEY: "real_Users_Experience",
      sortable: true,
      label: "Real Users Score",
    },
    {
      id: 3,
      KEY: "real_time_performance_score",
      sortable: true,
      label: "Real Time Score",
    },
    {
      id: 4,
      KEY: "Ads_Mobile_Page_Speed_Score",
      sortable: true,
      label: "Ads Mobile Score",
    },
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getSortedData = (arrayToSort: any[]) => {
    // If no sorting key is set, return the original array
    if (!sort.keytoSort || !sort.direction) {
      return arrayToSort;
    }

    // Clone the array to avoid mutating the original array
    return arrayToSort.slice().sort((a, b) => {
      const keytoSort = sort.keytoSort;

      if (keytoSort === undefined) {
        return 0; // Return 0 if the sorting key is undefined
      }

      const aValue = a[keytoSort];
      const bValue = b[keytoSort];

      // Handle undefined values for sorting
      if (aValue === undefined) {
        return 1; // Move undefined values to the end
      }
      if (bValue === undefined) {
        return -1; // Move undefined values to the end
      }

      // Handle "N/A" values for sorting
      if (aValue === "") {
        return 1; // Move "N/A" values to the end
      }
      if (bValue === "") {
        return -1; // Move "N/A" values to the end
      }

      // Custom sorting for labels
      if (keytoSort === "labels") {
        // Sort based on the length of labels array first
        const aLabelCount = Array.isArray(aValue) ? aValue.length : 0;
        const bLabelCount = Array.isArray(bValue) ? bValue.length : 0;

        // For descending order, empty labels come first
        if (sort.direction === "desc") {
          if (aLabelCount === 0 && bLabelCount > 0) return -1; // a is empty, b is not
          if (bLabelCount === 0 && aLabelCount > 0) return 1; // b is empty, a is not
        } else {
          // For ascending order, empty labels come last
          if (aLabelCount === 0 && bLabelCount > 0) return 1; // a is empty, b is not
          if (bLabelCount === 0 && aLabelCount > 0) return -1; // b is empty, a is not
        }
      }

      // Perform sorting based on the direction and type of values
      if (typeof aValue === "string" && typeof bValue === "string") {
        // String comparison (localeCompare for correct string sorting)
        return sort.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (typeof aValue === "number" && typeof bValue === "number") {
        // Numeric comparison
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      } else if (aValue instanceof Date && bValue instanceof Date) {
        // Date comparison
        return sort.direction === "asc"
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      } else {
        // Fallback to string comparison if values are of different types
        return sort.direction === "asc"
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }
    });
  };

  const sortedRows = getSortedData(rows || []);
  const paginatedData = sortedRows?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );


  return (
    <>
      <TableContainer
        // component={Paper}
        sx={{ maxHeight: 800, background: "#ffffff", mt: "10px" }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header) => (
                <TableCell key={header.id}>
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData &&
              paginatedData?.map((row) => (
                <React.Fragment key={row.cron_id}>
                  <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                    <TableCell align="left">
                      <Link
                        data-tracker="158"
                        href={row?.page_url}
                        color="secondary"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {row?.page_name}
                      </Link>
                    </TableCell>
                    <TableCell align="left">
                      {Math.floor(row.Overall_Speed_Performance_Score * 100)}%
                    </TableCell>
                    <TableCell align="left">
                      {Math.floor(row.real_Users_Experience * 100)}%
                    </TableCell>
                    <TableCell align="left">
                      {Math.floor(row.real_time_performance_score * 100)}%
                    </TableCell>
                    <TableCell align="left">
                      {Math.floor(row.Ads_Mobile_Page_Speed_Score * 100)}%
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            {!paginatedData?.length && (
              <TableRow>
                <TableCell colSpan={tableHeaders.length + 1} align="center">
                  <Typography variant="h6">
                    No data available for the selected filters
                  </Typography>{" "}
                  {/* No data available message */}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Stack
          spacing={1}
          direction="row"
          justifyContent="center"
          alignItems="center"
          mt={1}
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={sortedRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
              display: "none",
            }}
          />
          <Pagination
            count={Math.ceil(rows?.length / rowsPerPage)}
            onChange={(_, value) => {
              setPage(value - 1);
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set("brpPage", (value - 1).toString());
                return next;
              }, { replace: true });
            }}
            page={page + 1}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={sortedRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".css-levciy-MuiTablePagination-displayedRows": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
              display: "none",
            }}
          />
        </Stack>
      </TableContainer>
    </>
  );
};

export default PerformanceDashboardBottomRatedPages;
